import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CAPTURE_ERROR_CODES, resolveCaptureError } from './captureErrors';
import { ADMIN_ORDERS_KEY } from '../../../api/admin-orders';
import { postCapturePayment } from '../../../api/admin-payments';
import { ADMIN_PAYOUTS_KEY } from '../../../api/admin-payouts';
import { publishToast } from '../../../components/feedback';

/**
 * AUT-223 — manual-capture orchestration for the order detail's Платёж tab.
 *
 * Its only caller is `CaptureActions`. Single unconditional POST (AUT-138
 * removed per-action TOTP step-up), then:
 *
 *   - **success** → invalidate `['admin','orders']` so the tab re-reads the
 *     payment (status, `capturedAmount`, `capturedAt`) instead of showing what
 *     it fetched a minute ago, AND `['admin','payouts']` because a capture is
 *     what CREATES the inspector's payout (AUT-273). Invalidate-only, never a
 *     `setQueryData` patch — the action envelope is `{from,to,amount,...}`, a
 *     different shape from `AdminOrderDetailDto`.
 *   - **rejection** → the specific server error is held in `error` for the
 *     confirmation dialog to render INLINE, and the dialog stays open. A toast
 *     would be the wrong surface: the operator's next move (refresh vs
 *     reconcile vs stop) depends on WHICH code came back, and that belongs
 *     next to the button they just pressed. We also invalidate, because a
 *     rejection usually means our view of the status was stale.
 *
 * AUT-301 added a code whose copy depends on its `details` rather than on the
 * code alone (`payment_hold_window_closed`), which is why the resolved error can
 * carry interpolation `params` — see `captureErrors.ts`.
 */

export interface UseCapturePaymentResult {
  /** True while the POST is in flight — the dialog disables its confirm. */
  busy: boolean;
  /** Resolved server-error text, `null` when nothing is pending. */
  error: string | null;
  /** Drop a held error (dialog close / re-open). */
  reset: () => void;
  /** Run the capture; resolves true on success, false on rejection. */
  run: (paymentId: string) => Promise<boolean>;
}

export const useCapturePayment = (): UseCapturePaymentResult => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => setError(null), []);

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ADMIN_ORDERS_KEY }),
      queryClient.invalidateQueries({ queryKey: ADMIN_PAYOUTS_KEY }),
    ]);
  };

  const run = async (paymentId: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await postCapturePayment(paymentId);
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 5000,
        message: t('orders.capture.successToast'),
      });
      return true;
    } catch (err: unknown) {
      const text = resolveCaptureError(err, CAPTURE_ERROR_CODES);
      setError(text.kind === 'key' ? t(text.key, text.params ?? {}) : text.text);
      // The status we gated the button on may have moved under us.
      await invalidate();
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, error, reset, run };
};
